import React, { useState, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  EDIT_LISTING_DEFAULT_TAB,
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { addToCart, selectIsListingInCart, selectAddToCartInProgress } from '../../ducks/cart.duck';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  RequireAuthModal,
  VerificationBadge,
  VerificationNudge,
  BuyerProtection,
} from '../../components';
import { isVerificationActive } from '../../util/categoryHelpers';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  trackListingView,
  fetchListingStats,
  fetchSimilarListings,
  fetchCrossSell,
} from './ListingPage.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionOverviewMaybe from './SectionOverviewMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import SocialProofBadges from './SocialProofBadges';
import EnvironmentalImpactSection from './EnvironmentalImpactSection';
import SectionSimilarItems from './SectionSimilarItems';
import SectionFAQ from './SectionFAQ';
import SectionCrossSell from './SectionCrossSell/SectionCrossSell';
import ShippingInfoSection from '../../components/OrderPanel/ShippingInfoSection';
import ListingAiCard from './ListingAiCard/ListingAiCard';
import CrossSellModal from '../../components/CrossSellModal/CrossSellModal';
import { ReportListingModal } from '../../components';
import useAmbientTune from './useAmbientTune';
import StarBackground from '../../components/StarBackground/StarBackground';
import useExtractPalette from '../../components/StarBackground/useExtractPalette';
import { trackListingPresence } from '../../util/api';
import useRequireAuthPrompt from '../../hooks/useRequireAuthPrompt';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const { authPrompt, requireAuthForAction, closeAuthPrompt } = useRequireAuthPrompt();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Track listing view and fetch stats on mount
  useEffect(() => {
    if (props.params?.id && props.onTrackListingView && props.onFetchListingStats) {
      Promise.resolve(props.onTrackListingView(props.params.id)).catch(e => {
        console.warn('[ListingPage] Error tracking view:', e?.message || e);
      });

      const syncEngagement = () => {
        Promise.resolve(trackListingPresence(props.params.id)).catch(e => {
          console.warn('[ListingPage] Error tracking live presence:', e?.message || e);
        });
        Promise.resolve(props.onFetchListingStats(props.params.id)).catch(e => {
          console.warn('[ListingPage] Error fetching stats:', e?.message || e);
        });
      };

      syncEngagement();
      const intervalId = window.setInterval(syncEngagement, 60000);

      return () => {
        window.clearInterval(intervalId);
      };
    }
  }, [props.params?.id, props.onTrackListingView, props.onFetchListingStats]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    // Social proof and similar items
    listingStats,
    fetchListingStatsInProgress,
    similarListings,
    fetchSimilarListingsInProgress,
    onTrackListingView,
    onFetchListingStats,
    onFetchSimilarListings,
    onUpdateFavorites,
    // Cart
    selectIsInCartFn,
    addToCartInProgress,
    onAddToCart,
    // Cross-sell
    crossSellListings,
    crossSellGroups,
    crossSellHeading,
    crossSellPartnerExtras,
    fetchCrossSellInProgress,
    onFetchCrossSell,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);

  // Fetch similar listings when listing data is available
  const listingForSimilar = getListing(listingId);
  const categoryLevel1ForSimilar = listingForSimilar?.attributes?.publicData?.categoryLevel1;

  useEffect(() => {
    if (mounted && props.params?.id && categoryLevel1ForSimilar && onFetchSimilarListings) {
      // Wrap in Promise.resolve to handle async errors from Redux thunks
      Promise.resolve(
        onFetchSimilarListings(categoryLevel1ForSimilar, props.params.id, config)
      ).catch(e => {
        console.warn('[ListingPage] Error fetching similar listings:', e?.message || e);
      });
    }
  }, [mounted, props.params?.id, categoryLevel1ForSimilar]);

  // Fetch cross-sell when listing is available
  useEffect(() => {
    if (mounted && props.params?.id && onFetchCrossSell) {
      Promise.resolve(onFetchCrossSell(props.params.id)).catch(e => {
        console.warn('[ListingPage] Error fetching cross-sell:', e?.message || e);
      });
    }
  }, [mounted, props.params?.id]);

  // Cross-sell modal state
  const [showCrossSellModal, setShowCrossSellModal] = useState(false);

  // Report listing modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // Wrapper: show cross-sell modal after add-to-cart on the main listing
  const handleAddToCartWithCrossSell = listing => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    if (!currentUser) {
      requireAuthForAction({
        action: 'add_to_cart',
        returnTo,
        source: 'listing_page',
      });
      return null;
    }

    const result = onAddToCart(listing);
    // Show modal if we have cross-sell suggestions
    if (crossSellListings?.length > 0) {
      setShowCrossSellModal(true);
    }
    return result;
  };

  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // Analyze listing hero image to tune ambient glow intensity
  const heroAmbientUrl = useMemo(() => {
    const img = currentListing?.images?.[0];
    return img?.attributes?.variants?.['scaled-small']?.url || null;
  }, [currentListing?.images]);
  useAmbientTune(heroAmbientUrl);
  const paletteData = useExtractPalette(heroAmbientUrl);

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = EDIT_LISTING_DEFAULT_TAB;

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
  const hasFaq = Array.isArray(publicData.faq) && publicData.faq.length > 0;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  // Serial verification data
  const serialVerified = publicData?.serialVerified;
  const serialExpiresAt = publicData?.serialExpiresAt;
  const verificationId = publicData?.verificationId;
  const serialVerificationDetails = publicData?.serialVerificationDetails;

  // Categories that support IMEI/serial verification
  const VERIFICATION_SUPPORTED_CATEGORIES = [
    'phones',
    'tablets',
    'laptops',
    'desktops',
    'iphone',
    'samsung',
    'google',
    'apple',
    'macbook',
    'ipad',
  ];

  // Check if this listing's category supports verification
  const categoryKeys = Object.keys(publicData || {}).filter(k => k.startsWith('categoryLevel'));
  const categoryValues = categoryKeys.map(k => publicData[k]).filter(Boolean);
  const categorySupportsVerification = categoryValues.some(
    cat =>
      cat &&
      VERIFICATION_SUPPORTED_CATEGORIES.some(supported => cat.toLowerCase().includes(supported))
  );

  // Positive-only: show badge only when verification is active (verified + not expired)
  const showVerificationBadge = categorySupportsVerification && isVerificationActive(publicData);

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : isNegotiation
    ? 'negotiation'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
    onRequireAuth: requireAuthForAction,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
    currentUser,
    location,
    onRequireAuth: requireAuthForAction,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
    currentUser,
    location,
    onRequireAuth: requireAuthForAction,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    location,
    onRequireAuth: requireAuthForAction,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // Product schema for Google rich results
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ? { noIndex: true } : {};

  // Enriched Product schema fields for SEO rich snippets
  const brandMaybe = publicData.device_brand
    ? { brand: { '@type': 'Brand', name: publicData.device_brand } }
    : {};
  const skuMaybe = currentListing.id ? { sku: currentListing.id.uuid } : {};
  const categoryMaybe = publicData.categoryLevel1
    ? { category: publicData.categoryLevel1 }
    : {};

  // Map condition publicData values to schema.org item condition URLs
  const conditionSchemaMap = {
    'device-brand-new': 'https://schema.org/NewCondition',
    'device-like-new-condition': 'https://schema.org/UsedCondition',
    'device-excellent-condition': 'https://schema.org/UsedCondition',
    'device-good-condition': 'https://schema.org/UsedCondition',
    'device-fair-condition': 'https://schema.org/UsedCondition',
    'device-poor-condition': 'https://schema.org/UsedCondition',
    'device-spares-and-repairs': 'https://schema.org/DamagedCondition',
  };
  const conditionValue = publicData.condition || publicData.device_condition;
  const itemConditionMaybe = conditionValue && conditionSchemaMap[conditionValue]
    ? { itemCondition: conditionSchemaMap[conditionValue] }
    : {};

  const sellerMaybe = authorDisplayName
    ? {
        seller: {
          '@type': 'Person',
          name: authorDisplayName,
        },
      }
    : {};

  const modelMaybe = publicData.device_name
    ? { model: publicData.device_name }
    : {};
  const colorMaybe = publicData.device_colour
    ? { color: publicData.device_colour }
    : {};
  const mpnMaybe = publicData.device_name
    ? { mpn: publicData.device_name }
    : {};

  // FAQPage schema from AI-generated FAQ data
  const faqSchema =
    publicData.faq?.length > 0
      ? {
          '@context': 'http://schema.org',
          '@type': 'FAQPage',
          mainEntity: publicData.faq.map(item => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.a,
            },
          })),
        }
      : null;

  // BreadcrumbList JSON-LD for category path display in Google SERPs
  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: config.marketplaceRootURL },
  ];
  let breadcrumbPosition = 2;
  if (publicData.categoryLevel1) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: breadcrumbPosition++,
      name: publicData.categoryLevel1,
      item: `${config.marketplaceRootURL}/c/${publicData.categoryLevel1}`,
    });
    if (publicData.categoryLevel2) {
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: breadcrumbPosition++,
        name: publicData.categoryLevel2,
        item: `${config.marketplaceRootURL}/c/${publicData.categoryLevel1}/${publicData.categoryLevel2}`,
      });
      if (publicData.categoryLevel3) {
        breadcrumbItems.push({
          '@type': 'ListItem',
          position: breadcrumbPosition++,
          name: publicData.categoryLevel3,
          item: `${config.marketplaceRootURL}/c/${publicData.categoryLevel1}/${publicData.categoryLevel2}/${publicData.categoryLevel3}`,
        });
      }
    }
  }
  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbPosition,
    name: title,
  });
  const breadcrumbSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  // Truncate meta description to first sentence, max 155 chars for optimal SERP display
  const metaDescription = description
    ? description.split(/[.\n]/)[0]?.substring(0, 155).trim() || description.substring(0, 155).trim()
    : '';

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={metaDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      {...noIndexMaybe}
      schema={[
        {
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description?.substring(0, 5000),
          name: schemaTitle,
          image: schemaImages,
          ...brandMaybe,
          ...skuMaybe,
          ...categoryMaybe,
          ...itemConditionMaybe,
          ...modelMaybe,
          ...colorMaybe,
          ...mpnMaybe,
          ...sellerMaybe,
          offers: {
            '@type': 'Offer',
            url: productURL,
            priceCurrency: 'GBP',
            ...priceForSchemaMaybe(price),
            ...availabilityMaybe,
            shippingDetails: {
              '@type': 'OfferShippingDetails',
              shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'GB',
              },
              deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                businessDays: {
                  '@type': 'QuantitativeValue',
                  minValue: 1,
                  maxValue: 3,
                },
              },
            },
            hasMerchantReturnPolicy: {
              '@type': 'MerchantReturnPolicy',
              applicableCountry: 'GB',
              returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
              merchantReturnDays: 14,
            },
          },
        },
        breadcrumbSchema,
        ...(faqSchema ? [faqSchema] : []),
      ]}
    >
      {paletteData?.palette && (
        <StarBackground
          colorMode="ambient"
          colors={paletteData.palette}
          density={350}
          shootingStars={false}
          intensity={paletteData.isMuted ? 'subtle' : 'normal'}
          parallaxEnabled
        />
      )}
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                currentUser={currentUser}
              />
            ) : null}
            {mounted && currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                currentUser={currentUser}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null}
            {showListingImage && (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
              />
            )}
            <div
              className={showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct}
            >
              {showListingImage ? (
                <>
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                  {showVerificationBadge && (
                    <VerificationBadge
                      status="verified"
                      size="medium"
                      showTooltip
                      verificationId={verificationId}
                      expiresAt={serialExpiresAt}
                      verificationDetails={serialVerificationDetails}
                    />
                  )}
                </>
              ) : (
                <H3 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
            </div>
            <div
              className={classNames(css.descriptionFaqRow, {
                [css.descriptionFaqRowSolo]: !hasFaq,
              })}
            >
              <div className={css.descriptionColumn}>
                <SectionOverviewMaybe text={description} />
                {isOwnListing && categorySupportsVerification && !isVerificationActive(publicData) && (
                  <VerificationNudge
                    listingId={listingId.uuid}
                    listingSlug={listingSlug}
                    isExpired={serialVerified === true && !isVerificationActive(publicData)}
                  />
                )}
                <BuyerProtection className={css.buyerProtectionMobileContent} />
              </div>
              {hasFaq ? (
                <aside className={css.faqColumn}>
                  <SectionFAQ faq={publicData.faq} />
                </aside>
              ) : null}
            </div>

            {/* AI Analysis Card — mobile (inline in main column) */}
            <ListingAiCard
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              className={css.listingAiCardMobile}
            />

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={classNames(css.productOrderPanel, {
                [css.imagesEnabled]: showListingImage,
              })}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <div className={css.orderPanelTitleWrapper}>
                  <H4 as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </H4>
                  {showVerificationBadge && (
                    <VerificationBadge
                      status="verified"
                      size="medium"
                      showTooltip
                      verificationId={verificationId}
                      expiresAt={serialExpiresAt}
                      verificationDetails={serialVerificationDetails}
                    />
                  )}
                </div>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              onUpdateFavorites={onUpdateFavorites}
              onAddToCart={handleAddToCartWithCrossSell}
              isInCart={selectIsInCartFn ? selectIsInCartFn(currentListing?.id?.uuid) : false}
              addToCartInProgress={addToCartInProgress}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              socialProofSection={
                <SocialProofBadges
                  className={css.socialProofDesktop}
                  recent24hViewCount={listingStats?.recent24hViewCount || 0}
                  liveViewerCount={listingStats?.liveViewerCount || 0}
                  wishlistCount={listingStats?.wishlistCount || 0}
                  cartCount={listingStats?.cartCount || 0}
                  showTrending={listingStats?.isTrending || false}
                />
              }
              environmentalImpactSection={<EnvironmentalImpactSection publicData={publicData} />}
              shippingInfoSection={
                <ShippingInfoSection
                  shippingEnabled={publicData?.shippingEnabled !== false}
                  pickupEnabled={publicData?.pickupEnabled || false}
                  freeShipping={
                    publicData?.freeShipping ||
                    publicData?.shipping?.freeShipping ||
                    publicData?.shippingPriceInSubunitsOneItem === 0
                  }
                  shippingPrice={
                    publicData?.shippingPriceInSubunitsOneItem
                      ? {
                          amount: publicData.shippingPriceInSubunitsOneItem,
                          currency: config.currency,
                        }
                      : null
                  }
                  itemLocation={
                    publicData?.location?.address ||
                    ensuredAuthor?.attributes?.profile?.publicData?.userCity
                  }
                  returnsDays={publicData?.returnsDays}
                  returnsPolicy={publicData?.returnsPolicy}
                  marketplaceCurrency={config.currency}
                />
              }
            />
            {/* AI Analysis Card — desktop (in order column) */}
            <ListingAiCard
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              className={css.listingAiCardDesktop}
            />
            {/* Cross-sell accessories — below AI card in order column */}
            <SectionCrossSell
              heading={crossSellHeading}
              groups={crossSellGroups}
              listings={crossSellListings}
              partnerExtras={crossSellPartnerExtras}
              isLoading={fetchCrossSellInProgress}
              sourceListingId={props.params?.id}
            />
          </div>
        </div>

        {/* Similar Items Carousel */}
        <SectionSimilarItems
          listings={similarListings}
          isLoading={fetchSimilarListingsInProgress}
          currentUser={currentUser}
          onUpdateFavorites={onUpdateFavorites}
        />

        {/* Report listing button — only for non-owners */}
        {!isOwnListing && currentUser && (
          <div className={css.reportSection}>
            <button
              type="button"
              className={css.reportButton}
              onClick={() => setShowReportModal(true)}
            >
              <FormattedMessage id="ListingPage.reportListing" defaultMessage="Report this listing" />
            </button>
          </div>
        )}

        {/* Report listing modal */}
        <ReportListingModal
          listingId={rawParams.id}
          listingTitle={currentListing?.attributes?.title}
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
        />
        <RequireAuthModal
          prompt={authPrompt}
          isOpen={!!authPrompt}
          onClose={closeAuthPrompt}
          onManageDisableScrolling={onManageDisableScrolling}
        />

        {/* Cross-sell modal — shown after add-to-cart */}
        <CrossSellModal
          isOpen={showCrossSellModal}
          onClose={() => setShowCrossSellModal(false)}
          onViewCart={() => {
            setShowCrossSellModal(false);
            history.push('/cart');
          }}
          crossSellHeading={crossSellHeading}
          crossSellListings={crossSellListings}
          onAddToCart={onAddToCart}
          sourceListingId={props.params?.id}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    // Social proof and similar items
    listingStats,
    fetchListingStatsInProgress,
    similarListings,
    fetchSimilarListingsInProgress,
    // Cross-sell
    crossSellListings,
    crossSellGroups,
    crossSellHeading,
    crossSellPartnerExtras,
    fetchCrossSellInProgress,
  } = state.ListingPage;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser: state.user.currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    // Social proof and similar items
    listingStats,
    fetchListingStatsInProgress,
    similarListings,
    fetchSimilarListingsInProgress,
    // Cross-sell
    crossSellListings,
    crossSellGroups,
    crossSellHeading,
    crossSellPartnerExtras,
    fetchCrossSellInProgress,
    // Cart
    selectIsInCartFn: listingId => selectIsListingInCart(state, listingId),
    addToCartInProgress: selectAddToCartInProgress(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  // Social proof and similar items
  onTrackListingView: listingId => dispatch(trackListingView(listingId)),
  onFetchListingStats: listingId => dispatch(fetchListingStats(listingId)),
  onFetchSimilarListings: (categoryLevel1, excludeListingId, config) =>
    dispatch(fetchSimilarListings(categoryLevel1, excludeListingId, config)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)),
  // Cross-sell
  onFetchCrossSell: listingId => dispatch(fetchCrossSell(listingId)),
  // Cart
  onAddToCart: listing => dispatch(addToCart(listing)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
